import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { KeyFacts, ProTip } from '../../components/LessonUI';

const CCompilerSetup = () => {
    return (
        <div className="premium-course-content">
            <div className="content-hero">
                <div className="hero-badge">Environment Setup</div>
                <h1>Compiler Setup (GCC, Clang)</h1>
                <p className="hero-lead">
                    Set up a professional C development environment with GCC or Clang compilers. Learn installation,
                    configuration, and essential compiler options for different operating systems.
                </p>
            </div>

            <div className="content-section">
                <h2>Understanding C Compilers</h2>
                <p>
                    A C compiler translates your human-readable C code into machine-executable binary code. Unlike
                    interpreted languages like Python, C code must be compiled before it can run. This compilation step
                    is what gives C its speed advantage - the resulting machine code runs directly on the CPU without
                    any intermediate interpretation layer.
                </p>
                <p>
                    The two most popular C compilers are GCC (GNU Compiler Collection) and Clang. GCC has been the
                    standard for decades, used in Linux development and countless projects. Clang is newer, designed
                    for better error messages and faster compilation, and is the default on macOS. Both are excellent
                    choices that support modern C standards. For this tutorial, we'll cover both so you can use
                    whichever fits your platform and preferences.
                </p>
                <p>
                    Microsoft Visual C++ (MSVC) is the primary compiler on Windows, but GCC and Clang also work on
                    Windows through MinGW or Cygwin. We'll cover Windows setup separately to ensure you have options.
                    The good news is that standard C code compiles identically across all these compilers - that's the
                    power of standardization.
                </p>

                <ProTip><ul>
                        <li><strong>GCC:</strong> Mature, excellent optimization, Linux standard</li>
                        <li><strong>Clang:</strong> Fast compilation, great error messages, macOS default</li>
                        <li><strong>MSVC:</strong> Windows integrated, Visual Studio support</li>
                        <li><strong>All Three:</strong> Support modern C standards, produce fast code</li>
                    </ul></ProTip>
            </div>

            <div className="content-section">
                <h2>Installing GCC on Linux</h2>
                <p>
                    Most Linux distributions come with GCC pre-installed or make it trivially easy to install. GCC was
                    developed for Unix-like systems, so Linux is its natural home. The installation process varies
                    slightly depending on your distribution, but it's straightforward in all cases.
                </p>
                <p>
                    On Ubuntu, Debian, and their derivatives, you'll use the APT package manager. The build-essential
                    package includes GCC, G++ (for C++), make, and other essential development tools. This is the
                    recommended approach as it ensures you have everything needed for C development, not just the
                    compiler.
                </p>

                <div className="code-example">
                    <CodeBlock language="bash" code={`# Ubuntu/Debian - Install GCC and development tools
sudo apt update
sudo apt install build-essential

# Verify installation
gcc --version
# Should show: gcc (Ubuntu 11.x.x...) or similar

# Red Hat/Fedora/CentOS - Install GCC
sudo dnf groupinstall "Development Tools"
# OR on older versions:
sudo yum groupinstall "Development Tools"

# Arch Linux - Install GCC
sudo pacman -S base-devel

# Check GCC location
which gcc
# Output: /usr/bin/gcc

# Test with a simple program
echo 'int main() { return 0; }' &gt; test.c
gcc test.c -o test
./test
echo $?  # Should output: 0 (success)
rm test test.c`} />
                </div>

                <p>
                    After installation, GCC will be available system-wide. You can compile C programs from any terminal.
                    The build-essential package also includes important headers and libraries that C programs commonly
                    need, so you won't encounter missing header errors for standard library functions.
                </p>
            </div>

            <div className="content-section">
                <h2>Installing Clang on Linux</h2>
                <p>
                    Clang is available on all major Linux distributions and can coexist peacefully with GCC. Many
                    developers keep both installed to test their code with multiple compilers, ensuring maximum
                    portability. Clang often provides more helpful error messages, making it excellent for learning.
                </p>

                <div className="code-example">
                    <CodeBlock language="bash" code={`# Ubuntu/Debian - Install Clang
sudo apt update
sudo apt install clang

# Fedora/Red Hat
sudo dnf install clang

# Arch Linux
sudo pacman -S clang

# Verify installation
clang --version
# Should show: clang version 14.x.x or later

# Clang can use GCC's standard library
clang test.c -o test
./test

# Both compilers available
which gcc    # /usr/bin/gcc
which clang  # /usr/bin/clang`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Installing on macOS</h2>
                <p>
                    macOS doesn't include compilers by default, but Apple provides them through Command Line Tools for
                    Xcode. These tools include Clang (Apple's customized version), standard libraries, and necessary
                    headers. You don't need to install the full Xcode IDE unless you're doing iOS/macOS development.
                </p>
                <p>
                    The xcode-select command triggers the installation of Command Line Tools. This lightweight package
                    gives you everything needed for C development without the multi-gigabyte Xcode application. Note
                    that on macOS, 'gcc' is actually an alias to Clang - Apple stopped including real GCC years ago.
                </p>

                <div className="code-example">
                    <CodeBlock language="bash" code={`# Install Command Line Tools (includes Clang)
xcode-select --install
# A dialog appears - click Install and follow prompts

# Verify installation
clang --version
# Output: Apple clang version 14.x.x

# Note: 'gcc' is aliased to clang on macOS
gcc --version
# Also shows: Apple clang version...

# To install actual GCC (optional), use Homebrew
# First install Homebrew if you haven't:
/bin/bash -c "$(curl -fsSL https://raw.githubusercontent.com/Homebrew/install/HEAD/install.sh)"

# Then install GCC
brew install gcc

# GCC will be available as gcc-12 or gcc-13
gcc-12 --version
# Shows: gcc (Homebrew GCC...)

# Test compilation
echo 'int main() { return 0; }' &gt; test.c
clang test.c -o test
./test
rm test test.c`} />
                </div>
            </div>

            <div className="content-section">
                <h2>Installing on Windows</h2>
                <p>
                    Windows requires more setup since it doesn't include a C compiler by default. You have several
                    options: MinGW-w64 (GCC for Windows), Clang for Windows, or Microsoft's MSVC through Visual Studio.
                    For learning C in a cross-platform way, MinGW-w64 or Clang are recommended as they match the
                    Unix-like behavior.
                </p>
                <p>
                    MinGW-w64 (Minimalist GNU for Windows) provides GCC and related tools that work natively on Windows.
                    The easiest way to install it is through MSYS2, which provides a Unix-like environment on Windows
                    including a package manager (pacman, like Arch Linux).
                </p>

                <div className="code-example">
                    <CodeBlock language="bash" code={`# Option 1: MinGW-w64 via MSYS2 (Recommended)
# 1. Download MSYS2 installer from: https://www.msys2.org/
# 2. Run installer, use default path: C:\\msys64
# 3. Open MSYS2 terminal and update:
pacman -Syu
# Close terminal when prompted, reopen and run again:
pacman -Syu

# 4. Install MinGW-w64 GCC
pacman -S mingw-w64-x86_64-gcc

# 5. Add to Windows PATH:
# Add C:\\msys64\\mingw64\\bin to your PATH environment variable
# (System Properties &gt; Environment Variables &gt; Path &gt; Edit &gt; New)

# 6. Verify (in new Command Prompt or PowerShell)
gcc --version
# Output: gcc (Rev...) 12.x.x

# Option 2: Clang via LLVM
# 1. Download from: https://releases.llvm.org/
# 2. Run installer, check "Add LLVM to PATH"
# 3. Verify installation:
clang --version

# Option 3: Visual Studio (MSVC)
# 1. Download Visual Studio Community (free)
# 2. Install "Desktop development with C++" workload
# 3. Use "Developer Command Prompt for VS"
cl /?  # Shows MSVC compiler help`} />
                </div>

                <p>
                    After installation, you can use GCC/Clang from Command Prompt, PowerShell, or any terminal. The
                    commands are identical to Linux/macOS, making it easy to follow tutorials designed for any platform.
                    If you use MSVC, commands differ slightly (cl instead of gcc), but the C code itself remains the same.
                </p>
            </div>

            <div className="content-section">
                <h2>Essential Compiler Flags</h2>
                <p>
                    Compilers have dozens of flags that control compilation behavior. Learning the essential ones helps
                    you write better code and catch errors early. These flags work identically in GCC and Clang, making
                    your workflow portable.
                </p>

                <div className="code-example">
                    <CodeBlock language="bash" code={`# Basic compilation
gcc program.c -o program
# Compiles program.c into executable named 'program'

# Specify C standard
gcc -std=c11 program.c -o program
# Use C11 standard (recommended)
gcc -std=c17 program.c -o program
# Use C17 standard (C11 with bug fixes)

# Enable all warnings (HIGHLY RECOMMENDED)
gcc -Wall program.c -o program
# Enables most useful warnings

# Even more warnings
gcc -Wall -Wextra program.c -o program
# Catches more potential issues

# Treat warnings as errors (for production code)
gcc -Wall -Wextra -Werror program.c -o program
# Won't compile if any warnings exist

# Pedantic mode (strict standards compliance)
gcc -std=c11 -pedantic program.c -o program
# Warns about non-standard features

# Optimization levels
gcc -O0 program.c -o program  # No optimization (default, good for debugging)
gcc -O1 program.c -o program  # Basic optimization
gcc -O2 program.c -o program  # Recommended for production
gcc -O3 program.c -o program  # Aggressive optimization
gcc -Os program.c -o program  # Optimize for size

# Debug information (for using debuggers like GDB)
gcc -g program.c -o program
# Includes debugging symbols

# Recommended development flags
gcc -std=c11 -Wall -Wextra -g program.c -o program

# Recommended production flags
gcc -std=c11 -Wall -Wextra -O2 program.c -o program`} />
                </div>

                <p>
                    Always use -Wall -Wextra during development. These flags catch common mistakes that would otherwise
                    cause subtle bugs. For example, using an uninitialized variable, forgetting a return statement, or
                    assigning instead of comparing in an if statement. The compiler can detect these issues, but only
                    if you ask it to warn you.
                </p>

                <ProTip><ul>
                        <li><strong>-std=c11:</strong> Use modern C standard</li>
                        <li><strong>-Wall -Wextra:</strong> Enable important warnings</li>
                        <li><strong>-g:</strong> Include debug info during development</li>
                        <li><strong>-O2:</strong> Good optimization for production</li>
                        <li><strong>-pedantic:</strong> Ensure standard compliance</li>
                    </ul></ProTip>
            </div>

            <div className="content-section">
                <h2>Testing Your Setup</h2>
                <p>
                    Let's verify your compiler works correctly by creating and compiling a complete C program. This
                    test ensures all parts of your setup function properly: the compiler, standard library headers,
                    and linker.
                </p>

                <div className="code-example">
                    <CodeBlock language="c" code={`/* Save as hello.c */
#include <stdio.h>

int main(void) {
    printf("Hello, C Programming!\\n");
    printf("Compiler: ");
    
    #ifdef __clang__
        printf("Clang %d.%d.%d\\n", 
            __clang_major__, __clang_minor__, __clang_patchlevel__);
    #elif defined(__GNUC__)
        printf("GCC %d.%d.%d\\n",
            __GNUC__, __GNUC_MINOR__, __GNUC_PATCHLEVEL__);
    #endif
    
    return 0;
}

/* Compile and run: */
// gcc -std=c11 -Wall hello.c -o hello
// ./hello
// Output: Hello, C Programming!
//         Compiler: GCC 11.3.0 (or similar)`} />
                </div>

                <p>
                    If this compiles and runs successfully, your C development environment is ready! You can now write,
                    compile, and execute C programs. In the next lesson, we'll write our first real C program and
                    understand what each part does.
                </p>
            </div>

            <div className="content-section">
                <h2>Summary & What's Next</h2>
                <KeyFacts><ul>
                        <li>GCC and Clang are the most popular C compilers</li>
                        <li>Linux: Install via build-essential or base-devel</li>
                        <li>macOS: Use Command Line Tools (includes Clang)</li>
                        <li>Windows: Use MinGW-w64, Clang, or MSVC</li>
                        <li>Always use -Wall -Wextra to catch warnings</li>
                        <li>Use -std=c11 or -std=c17 for modern C</li>
                        <li>Add -g for debugging, -O2 for optimization</li>
                        <li>Test your setup with a simple program</li>
                    </ul></KeyFacts>
            </div>

        </div>
    );
};

export default CCompilerSetup;



